'use client';
import * as React from 'react';
import invariant from 'invariant';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import { styled, SxProps, type Theme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { Link } from '../shared/Link';
import { Navigation, NavigationPageItem } from '../AppProvider';
import { getItemPath, getItemTitle } from '../shared/navigation';
import {
  NavigationContext,
  DashboardSidebarPageItemContext,
} from '../shared/context';

import { MINI_DRAWER_WIDTH } from './shared';

const NavigationListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: 8,
  '& .MuiSvgIcon-root': {
    color: (theme.vars ?? theme).palette.action.active,
  },
  '& .MuiAvatar-root': {
    backgroundColor: (theme.vars ?? theme).palette.action.active,
  },
  '&.Mui-selected': {
    '& .MuiSvgIcon-root': {
      color: (theme.vars ?? theme).palette.primary.dark,
    },
    '& .MuiTypography-root': {
      color: (theme.vars ?? theme).palette.primary.dark,
    },
    '& .MuiListItemIcon-root': {
      color: (theme.vars ?? theme).palette.primary.dark,
    },
    '& .MuiAvatar-root': {
      backgroundColor: (theme.vars ?? theme).palette.primary.dark,
    },
    '& .MuiTouchRipple-child': {
      backgroundColor: (theme.vars ?? theme).palette.primary.dark,
    },
  },
}));

export interface DashboardSidebarPageItemProps {
  /**
   * Link `href` for when the item is rendered as a link.
   * @default getItemPath(navigationContext, item)
   */
  href?: string;
  /**
   * If `true`, expands any nested navigation in the item, otherwise collapse it.
   * @default false
   */
  expanded?: boolean;
  /**
   * Use to apply selected styling.
   * @default false
   */
  selected?: boolean;
  /**
   * If `true`, the item is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * Navigation page item definition.
   */
  item: NavigationPageItem;
  /**
   * The component used to render the item as a link.
   * @default Link
   */
  LinkComponent?: React.ElementType;
}

export interface DashboardSidebarPageItemContextProps extends Partial<DashboardSidebarPageItemProps> {
  id: string;
  isMini?: boolean;
  isSidebarFullyExpanded?: boolean;
  isSidebarFullyCollapsed?: boolean;
  onClick: (itemId: string, item: NavigationPageItem) => void;
  renderNestedNavigation: (subNavigation: Navigation) => React.ReactNode;
}

const LIST_ITEM_ICON_SIZE = 34;
/**
 *
 * Demos:
 *
 * - [Dashboard Layout](https://mui.com/toolpad/core/react-dashboard-layout/)
 *
 * API:
 *
 * - [DashboardSidebarPageItem API](https://mui.com/toolpad/core/api/dashboard-sidebar-page-item)
 */
function DashboardSidebarPageItem(props: DashboardSidebarPageItemProps) {
  const navigationContext = React.useContext(NavigationContext);
  const pageItemContextProps = React.useContext(
    DashboardSidebarPageItemContext
  );

  invariant(pageItemContextProps, 'No navigation page item context provided.');

  const contextAwareProps = {
    ...pageItemContextProps,
    ...props,
  };

  const {
    id,
    item,
    onClick,
    isMini = false,
    expanded = false,
    selected = false,
    disabled = false,
    renderNestedNavigation,
    isSidebarFullyExpanded = true,
    isSidebarFullyCollapsed = false,
    LinkComponent: LinkComponentProp,
    href = getItemPath(navigationContext, item),
  } = contextAwareProps;

  const [hoveredMiniSidebarItemId, setHoveredMiniSidebarItemId] =
    React.useState<string | null>(null);

  const handleClick = React.useCallback(() => {
    onClick(id, item);
  }, [id, item, onClick]);

  let nestedNavigationCollapseSx: SxProps<Theme> = { display: 'none' };
  if (isMini && isSidebarFullyCollapsed) {
    nestedNavigationCollapseSx = {
      fontSize: 18,
      top: '41.5%',
      right: '2px',
      position: 'absolute',
      transform: 'translateY(-50%) rotate(-90deg)',
    };
  } else if (!isMini && isSidebarFullyExpanded) {
    nestedNavigationCollapseSx = {
      ml: 0.5,
      transform: `rotate(${expanded ? 0 : -90}deg)`,
      transition: (theme: Theme) =>
        theme.transitions.create('transform', {
          duration: 100,
          easing: theme.transitions.easing.sharp,
        }),
    };
  }

  const hasExternalHref =
    href.startsWith('http://') || href.startsWith('https://');

  const LinkComponent = LinkComponentProp ?? (hasExternalHref ? 'a' : Link);

  const title = getItemTitle(item);

  const listItem = (
    <ListItem
      {...(item.children && isMini
        ? {
            onMouseEnter: () => {
              setHoveredMiniSidebarItemId(id);
            },
            onMouseLeave: () => {
              setHoveredMiniSidebarItemId(null);
            },
          }
        : {})}
      sx={{
        py: 0,
        px: 1,
        overflowX: 'hidden',
      }}
    >
      <NavigationListItemButton
        selected={selected}
        disabled={disabled}
        sx={{
          px: 1.4,
          height: isMini ? 60 : 48,
        }}
        {...(item.children && !isMini
          ? {
              onClick: handleClick,
            }
          : {})}
        {...(!item.children
          ? {
              LinkComponent,
              ...(hasExternalHref
                ? {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  }
                : {}),
              href,
              onClick: handleClick,
            }
          : {})}
      >
        {item.icon || isMini ? (
          <Box
            sx={
              isMini
                ? {
                    left: '50%',
                    position: 'absolute',
                    top: 'calc(50% - 6px)',
                    transform: 'translate(-50%, -50%)',
                  }
                : {}
            }
          >
            <ListItemIcon
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: LIST_ITEM_ICON_SIZE,
              }}
            >
              {item.icon ?? null}
              {!item.icon && isMini ? (
                <Avatar
                  sx={{
                    fontSize: 12,
                    width: LIST_ITEM_ICON_SIZE - 7,
                    height: LIST_ITEM_ICON_SIZE - 7,
                  }}
                >
                  {title
                    .split(' ')
                    .slice(0, 2)
                    .map((titleWord) => titleWord.charAt(0).toUpperCase())}
                </Avatar>
              ) : null}
            </ListItemIcon>
            {isMini ? (
              <Typography
                variant="caption"
                sx={{
                  bottom: -18,
                  left: '50%',
                  fontSize: 10,
                  fontWeight: 500,
                  overflow: 'hidden',
                  textAlign: 'center',
                  position: 'absolute',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  transform: 'translateX(-50%)',
                  maxWidth: MINI_DRAWER_WIDTH - 28,
                }}
              >
                {title}
              </Typography>
            ) : null}
          </Box>
        ) : null}
        {!isMini ? (
          <ListItemText
            primary={title}
            sx={{
              ml: 1.2,
              zIndex: 1,
              whiteSpace: 'nowrap',
            }}
          />
        ) : null}
        {item.action && !isMini && isSidebarFullyExpanded ? item.action : null}
        {item.children ? (
          <ExpandMoreIcon sx={nestedNavigationCollapseSx} />
        ) : null}
      </NavigationListItemButton>
      {item.children && isMini ? (
        <Grow in={id === hoveredMiniSidebarItemId}>
          <Box
            sx={{
              pl: '6px',
              position: 'fixed',
              left: MINI_DRAWER_WIDTH - 2,
            }}
          >
            <Paper
              sx={{
                pt: 0.5,
                pb: 0.5,
                transform: 'translateY(calc(50% - 30px))',
              }}
            >
              {renderNestedNavigation(item.children)}
            </Paper>
          </Box>
        </Grow>
      ) : null}
    </ListItem>
  );

  return (
    <React.Fragment key={id}>
      {listItem}
      {item.children && !isMini ? (
        <Collapse in={expanded} unmountOnExit timeout="auto">
          {renderNestedNavigation(item.children)}
        </Collapse>
      ) : null}
    </React.Fragment>
  );
}

DashboardSidebarPageItem.propTypes /* remove-proptypes */ = {
  /**
   * Link `href` for when the item is rendered as a link.
   * @default getItemPath(navigationContext, item)
   */
  href: PropTypes.string,
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * If `true`, the item is disabled.
   * @default false
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, expands any nested navigation in the item, otherwise collapse it.
   * @default false
   */
  expanded: PropTypes.bool,
  /**
   * Use to apply selected styling.
   * @default false
   */
  selected: PropTypes.bool,
  /**
   * The component used to render the item as a link.
   * @default Link
   */
  LinkComponent: PropTypes.elementType,
  /**
   * Navigation page item definition.
   */
  item: PropTypes.shape({
    icon: PropTypes.node,
    action: PropTypes.node,
    title: PropTypes.string,
    pattern: PropTypes.string,
    segment: PropTypes.string,
    kind: PropTypes.oneOf(['page']),
    children: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.shape({
          title: PropTypes.string.isRequired,
          kind: PropTypes.oneOf(['header']).isRequired,
        }),
        PropTypes.shape({
          kind: PropTypes.oneOf(['divider']).isRequired,
        }),
      ]).isRequired
    ),
  }).isRequired,
} as any;

export { DashboardSidebarPageItem };
